/**
 * Motorcycle Hooks
 * ================
 * React Query hooks for motorcycle operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motorcycleApi } from '@/services/api'
import type {
  HistoryEntry,
  HistoryEntryCreate,
  HistoryEntryUpdate,
  Motorcycle,
  MotorcycleCreate,
  MotorcycleSummary,
  MotorcycleUpdate,
  ServiceRecord,
  ServiceRecordCreate,
  ServiceRecordUpdate,
} from '@/types'

// =============================================================================
// Query Keys
// =============================================================================

export const motorcycleKeys = {
  all: ['motorcycles'] as const,
  lists: () => [...motorcycleKeys.all, 'list'] as const,
  list: () => [...motorcycleKeys.lists()] as const,
  details: () => [...motorcycleKeys.all, 'detail'] as const,
  detail: (id: string) => [...motorcycleKeys.details(), id] as const,
  service: (id: string) => [...motorcycleKeys.detail(id), 'service'] as const,
  history: (id: string) => [...motorcycleKeys.detail(id), 'history'] as const,
  parts: (id: string) => [...motorcycleKeys.detail(id), 'parts'] as const,
}

// =============================================================================
// Motorcycle Hooks
// =============================================================================

/**
 * Fetch all motorcycles (for sidebar)
 */
export function useMotorcycles() {
  return useQuery({
    queryKey: motorcycleKeys.list(),
    queryFn: async () => {
      const response = await motorcycleApi.list()
      return response.data as MotorcycleSummary[]
    },
  })
}

/**
 * Fetch a single motorcycle by ID
 */
export function useMotorcycle(id: string) {
  return useQuery({
    queryKey: motorcycleKeys.detail(id),
    queryFn: async () => {
      const response = await motorcycleApi.get(id)
      return response.data as Motorcycle
    },
    enabled: !!id,
  })
}

/**
 * Create a new motorcycle
 */
export function useCreateMotorcycle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: MotorcycleCreate) => motorcycleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.lists() })
    },
  })
}

/**
 * Update an existing motorcycle
 */
export function useUpdateMotorcycle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MotorcycleUpdate }) =>
      motorcycleApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.lists() })
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.detail(id) })
    },
  })
}

/**
 * Delete a motorcycle
 */
export function useDeleteMotorcycle() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => motorcycleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.lists() })
    },
  })
}

// =============================================================================
// Service Record Hooks
// =============================================================================

/**
 * Fetch service records for a motorcycle
 */
export function useServiceRecords(bikeId: string) {
  return useQuery({
    queryKey: motorcycleKeys.service(bikeId),
    queryFn: async () => {
      const response = await motorcycleApi.listServiceRecords(bikeId)
      return response.data as ServiceRecord[]
    },
    enabled: !!bikeId,
  })
}

/**
 * Create a service record
 */
export function useCreateServiceRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, data }: { bikeId: string; data: ServiceRecordCreate }) =>
      motorcycleApi.createServiceRecord(bikeId, data),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.service(bikeId) })
    },
  })
}

/**
 * Update a service record
 */
export function useUpdateServiceRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      bikeId,
      recordId,
      data,
    }: {
      bikeId: string
      recordId: number
      data: ServiceRecordUpdate
    }) => motorcycleApi.updateServiceRecord(bikeId, recordId, data),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.service(bikeId) })
    },
  })
}

/**
 * Delete a service record
 */
export function useDeleteServiceRecord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, recordId }: { bikeId: string; recordId: number }) =>
      motorcycleApi.deleteServiceRecord(bikeId, recordId),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.service(bikeId) })
    },
  })
}

// =============================================================================
// History Entry Hooks
// =============================================================================

/**
 * Fetch history entries for a motorcycle
 */
export function useHistoryEntries(bikeId: string) {
  return useQuery({
    queryKey: motorcycleKeys.history(bikeId),
    queryFn: async () => {
      const response = await motorcycleApi.listHistoryEntries(bikeId)
      return response.data as HistoryEntry[]
    },
    enabled: !!bikeId,
  })
}

/**
 * Create a history entry
 */
export function useCreateHistoryEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, data }: { bikeId: string; data: HistoryEntryCreate }) =>
      motorcycleApi.createHistoryEntry(bikeId, data),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.history(bikeId) })
    },
  })
}

/**
 * Update a history entry
 */
export function useUpdateHistoryEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      bikeId,
      entryId,
      data,
    }: {
      bikeId: string
      entryId: number
      data: HistoryEntryUpdate
    }) => motorcycleApi.updateHistoryEntry(bikeId, entryId, data),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.history(bikeId) })
    },
  })
}

/**
 * Delete a history entry
 */
export function useDeleteHistoryEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, entryId }: { bikeId: string; entryId: number }) =>
      motorcycleApi.deleteHistoryEntry(bikeId, entryId),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.history(bikeId) })
    },
  })
}

// =============================================================================
// Parts Hooks
// =============================================================================

/**
 * Fetch parts associated with a motorcycle
 */
export function useMotorcycleParts(bikeId: string) {
  return useQuery({
    queryKey: motorcycleKeys.parts(bikeId),
    queryFn: async () => {
      const response = await motorcycleApi.listParts(bikeId)
      return response.data
    },
    enabled: !!bikeId,
  })
}

/**
 * Associate a part with a motorcycle
 */
export function useAssociatePart() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, productId }: { bikeId: string; productId: number }) =>
      motorcycleApi.associatePart(bikeId, productId),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.parts(bikeId) })
    },
  })
}

/**
 * Disassociate a part from a motorcycle
 */
export function useDisassociatePart() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ bikeId, productId }: { bikeId: string; productId: number }) =>
      motorcycleApi.disassociatePart(bikeId, productId),
    onSuccess: (_, { bikeId }) => {
      queryClient.invalidateQueries({ queryKey: motorcycleKeys.parts(bikeId) })
    },
  })
}
